import React, { useId, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronDown,
  FileSearch,
  Home,
  RefreshCw,
  SearchCheck,
  Sparkles,
} from "lucide-react";
import { normalizeAnalysisState } from "../utils/atsAnalysis";

const clampScore = (value) => Math.max(0, Math.min(100, Number(value) || 0));

const sanitizeKeywords = (items) =>
  Array.isArray(items)
    ? items.map((item) => String(item || "").trim()).filter(Boolean)
    : [];

function getScoreMeta(score) {
  if (score >= 80) {
    return {
      label: "Strong match",
      message: "Your CV shows strong alignment with this role.",
      color: "#2563eb",
      soft: "bg-blue-50 text-blue-700 border-blue-100",
    };
  }
  if (score >= 60) {
    return {
      label: "Good foundation",
      message: "Your CV has a good foundation, with a few important gaps to review.",
      color: "#4f46e5",
      soft: "bg-indigo-50 text-indigo-700 border-indigo-100",
    };
  }
  if (score >= 40) {
    return {
      label: "Needs improvement",
      message: "Several role requirements could be represented more clearly.",
      color: "#d97706",
      soft: "bg-amber-50 text-amber-800 border-amber-100",
    };
  }
  return {
    label: "Low alignment",
    message: "Your CV currently has limited alignment with this role.",
    color: "#dc2626",
    soft: "bg-red-50 text-red-700 border-red-100",
  };
}

function ScoreRing({ value, label }) {
  const score = clampScore(value);
  const reduceMotion = useReducedMotion();
  const id = useId().replace(/:/g, "");
  const radius = 77;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - score / 100);
  const meta = getScoreMeta(score);

  return (
    <div
      className="relative h-36 w-36 shrink-0 sm:h-40 sm:w-40 lg:h-44 lg:w-44"
      role="img"
      aria-label={`${label}: ${score.toFixed(1)} out of 100. ${meta.label}.`}
    >
      <svg viewBox="0 0 176 176" className="h-full w-full -rotate-90" aria-hidden="true">
        <defs>
          <linearGradient id={`score-${id}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#2563eb" />
            <stop offset="100%" stopColor={meta.color} />
          </linearGradient>
        </defs>
        <circle cx="88" cy="88" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="10" />
        <motion.circle
          cx="88"
          cy="88"
          r={radius}
          fill="none"
          stroke={`url(#score-${id})`}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={reduceMotion ? false : { strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: reduceMotion ? 0 : 0.85, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className="text-3xl font-extrabold tracking-tight text-slate-950">
          {score.toFixed(1)}
        </span>
        <span className="mt-1 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
          out of 100
        </span>
      </div>
    </div>
  );
}

function KeywordCard({ id, title, description, items, type }) {
  const [expanded, setExpanded] = useState(false);
  const visibleItems = expanded ? items : items.slice(0, 8);
  const isMatched = type === "matched";

  return (
    <section id={id} className="rounded-3xl border border-slate-200/80 bg-white p-4 shadow-[0_20px_55px_-40px_rgba(15,23,42,0.35)] sm:p-5">
      <div className="flex items-start gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${isMatched ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
          {isMatched ? <CheckCircle2 aria-hidden="true" className="h-5 w-5" /> : <AlertTriangle aria-hidden="true" className="h-5 w-5" />}
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-950">{title}</h2>
          <p className="mt-1 text-sm leading-6 text-slate-600">{description}</p>
        </div>
      </div>

      {items.length ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {visibleItems.map((keyword, index) => (
            <span
              key={`${keyword}-${index}`}
              className={`inline-flex max-w-full items-center gap-1.5 break-words rounded-full border px-2.5 py-1 text-sm font-semibold ${isMatched ? "border-emerald-200 bg-emerald-50/70 text-emerald-800" : "border-amber-200 bg-amber-50/80 text-amber-900"}`}
            >
              {isMatched && <Check aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />}
              {keyword}
            </span>
          ))}
        </div>
      ) : (
        <div className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
          {isMatched
            ? "No matched skills were returned for this analysis."
            : "No missing keywords were identified in this analysis."}
        </div>
      )}

      {items.length > 8 && (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          className="mt-4 inline-flex items-center gap-1.5 rounded-lg text-sm font-bold text-blue-700 outline-none transition hover:text-blue-800 focus:ring-4 focus:ring-blue-100"
        >
          {expanded ? "Show fewer" : `Show all ${items.length}`}
          <ChevronDown aria-hidden="true" className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
      )}
    </section>
  );
}

function EmptyResults() {
  const navigate = useNavigate();

  return (
    <main className="relative min-h-[65vh] overflow-hidden bg-slate-50 px-4 py-10 sm:px-6">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-gradient-to-b from-blue-50 to-transparent" />
      <section className="relative mx-auto max-w-xl rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-[0_24px_70px_-38px_rgba(37,99,235,0.3)] sm:p-8">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
          <FileSearch aria-hidden="true" className="h-7 w-7" />
        </div>
        <h1 className="mt-5 text-2xl font-extrabold tracking-tight text-slate-950 sm:text-3xl">
          Your analysis results are no longer available.
        </h1>
        <p className="mx-auto mt-3 max-w-lg text-sm leading-6 text-slate-600 sm:text-base">
          Results are available immediately after an analysis. Upload your CV again to create a fresh ATS report.
        </p>
        <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
          <button onClick={() => navigate("/upload")} className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-blue-200 transition hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-200">
            <RefreshCw aria-hidden="true" className="h-4 w-4" /> Analyse a Resume
          </button>
          <button onClick={() => navigate("/")} className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:border-blue-200 hover:text-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-100">
            <Home aria-hidden="true" className="h-4 w-4" /> Return Home
          </button>
        </div>
      </section>
    </main>
  );
}

export default function ResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const state = location.state;

  if (!state || typeof state !== "object") return <EmptyResults />;

  const analysis = normalizeAnalysisState(state);
  const isV2 = analysis?.kind === "v2";
  const matchPercentage = clampScore(analysis?.roleMatchScore);
  const atsScore = clampScore(analysis?.overallScore);
  const atsReadinessScore = clampScore(analysis?.atsReadinessScore);
  const matchedKeywords = sanitizeKeywords(analysis?.matchedKeywords);
  const missingKeywords = sanitizeKeywords(analysis?.missingKeywords);
  const name = typeof state.name === "string" ? state.name : "";
  const email = typeof state.email === "string" ? state.email : "";
  const originalResume = typeof state.originalResume === "string" ? state.originalResume : "";
  const jobDescription = typeof state.jobDescription === "string" ? state.jobDescription : "";
  const hasResultData = Boolean(originalResume || matchedKeywords.length || missingKeywords.length || atsScore || matchPercentage);

  if (!hasResultData) return <EmptyResults />;

  const scoreMeta = isV2 ? {...getScoreMeta(atsScore), label: analysis.label, message: analysis.summary} : getScoreMeta(atsScore);

  const goToGenerate = () => {
    navigate("/generate-resume", {
      state: {
        name,
        email,
        missingKeywords: analysis.legacyMissingKeywords,
        originalResume,
        jobDescription,
        optimizationHandoff: analysis.optimizationHandoff,
        analysisContext: analysis.analysisContext,
        comparisonContext: analysis.comparisonContext,
      },
    });
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-50 px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-gradient-to-b from-blue-50 to-transparent" />
      <div className="pointer-events-none absolute -left-40 top-48 h-96 w-96 rounded-full bg-cyan-100/35 blur-3xl" />
      <div className="pointer-events-none absolute -right-40 top-16 h-96 w-96 rounded-full bg-blue-100/45 blur-3xl" />

      <motion.main
        className="relative mx-auto w-full max-w-6xl"
        initial={reduceMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <header className="max-w-3xl">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-blue-700 shadow-sm">
            <SearchCheck aria-hidden="true" className="h-3.5 w-3.5" /> ATS Analysis Complete
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">
            Your ATS match report <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">is ready.</span>
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">
            See how your CV aligns with the job description, where it performs well, and what to improve next.
          </p>
          {name && <p className="mt-2 text-sm font-semibold text-slate-500">Prepared for {name}</p>}
        </header>

        <section className="mt-5 rounded-3xl border border-blue-100 bg-white p-4 shadow-[0_24px_70px_-38px_rgba(37,99,235,0.35)] sm:p-6">
          <div className="grid items-center gap-5 md:grid-cols-[auto_1fr]">
            <div className="flex justify-center"><ScoreRing value={atsScore} label={isV2 ? "Overall application strength" : "ATS score"} /></div>
            <div>
              <div className={`inline-flex rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] ${scoreMeta.soft}`}>{scoreMeta.label}</div>
              <h2 className="mt-3 text-2xl font-bold text-slate-950 sm:text-3xl">{scoreMeta.message}</h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">Review the requirements already evidenced and the gaps below before creating your optimised resume.</p>
              <p className="mt-1 text-xs leading-5 text-slate-500">{isV2 ? "Overall strength is calculated as 35% ATS readiness and 65% role match." : "ATS score evaluates overall resume quality; job-description match measures alignment with this specific role."}</p>

              <div className="mt-4 grid gap-2.5 sm:grid-cols-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-3.5 py-3">
                  <p className="text-2xl font-extrabold text-slate-950">{(isV2 ? atsReadinessScore : matchPercentage).toFixed(1)}</p>
                  <p className="mt-0.5 text-xs font-bold uppercase tracking-[0.08em] text-slate-500">{isV2 ? "ATS Readiness" : "Job-description match"}</p>
                </div>
                <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 px-3.5 py-3">
                  <p className="text-2xl font-extrabold text-emerald-800">{isV2 ? matchPercentage.toFixed(1) : matchedKeywords.length}</p>
                  <p className="mt-0.5 text-xs font-bold uppercase tracking-[0.08em] text-emerald-700">{isV2 ? "Role Match" : "Matched skills"}</p>
                </div>
                <div className="rounded-xl border border-amber-100 bg-amber-50/60 px-3.5 py-3">
                  <p className="text-2xl font-extrabold text-amber-800">{missingKeywords.length}</p>
                  <p className="mt-0.5 text-xs font-bold uppercase tracking-[0.08em] text-amber-700">Requirements to review</p>
                </div>
              </div>
              <p className="mt-3 text-xs leading-5 text-slate-500">ATS scores are guidance, not a guarantee of recruiter decisions.</p>
              {isV2 && analysis.layoutLimitation && <p className="mt-1 text-xs leading-5 text-slate-500"><strong>Limited layout inspection:</strong> {analysis.layoutLimitation}</p>}
            </div>
          </div>
        </section>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <KeywordCard id="matched-skills" title={isV2 ? "Matched requirements" : "Matched skills"} description={isV2 ? "Exact and strong-equivalent requirements supported by resume evidence." : "Skills and keywords found in both your CV and this job description."} items={matchedKeywords} type="matched" />
          <KeywordCard id="missing-keywords" title={isV2 ? "Requirements to review" : "Missing keywords"} description={isV2 ? "Partial, inferred, and missing requirements. Add or clarify them only where truthful." : "Add these only where they truthfully reflect your experience."} items={missingKeywords} type="missing" />
        </div>

        <section className="mt-4 overflow-hidden rounded-3xl border border-blue-200 bg-gradient-to-br from-blue-600 to-indigo-700 p-5 text-white shadow-xl shadow-blue-200/60 sm:p-6">
          <div className="items-center justify-between gap-6 lg:flex">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 text-sm font-bold text-blue-100"><Sparkles aria-hidden="true" className="h-4 w-4" /> Your next step</div>
              <h2 className="mt-1.5 text-xl font-extrabold sm:text-2xl">Turn these insights into a stronger resume.</h2>
              <p className="mt-2 text-sm leading-6 text-blue-100">Generate an optimised version using the same resume and keyword analysis. Existing usage and premium rules still apply.</p>
            </div>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row lg:mt-0 lg:shrink-0 lg:flex-col">
              <button onClick={goToGenerate} className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3.5 text-sm font-bold text-blue-700 shadow-lg transition hover:-translate-y-0.5 hover:bg-blue-50 focus:outline-none focus:ring-4 focus:ring-white/40">
                Generate My Optimised Resume <ArrowRight aria-hidden="true" className="h-4 w-4" />
              </button>
              <button onClick={() => navigate("/upload")} className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/30 bg-white/10 px-5 py-3 text-sm font-bold text-white transition hover:bg-white/20 focus:outline-none focus:ring-4 focus:ring-white/30">
                <RefreshCw aria-hidden="true" className="h-4 w-4" /> Analyse Another Job
              </button>
            </div>
          </div>
        </section>
      </motion.main>
    </div>
  );
}
